import { useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { ModalForm, Select, Text } from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import { labelWithHint, searchLabelAndHint } from "@components/OptionWithHint";

// O erro da lib fica embaixo do campo, fora do fluxo: com ele, a frase de apoio desce
const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ $hasError }) => ($hasError ? 26 : 6)}px;
`;

const FIELD_ID = "confidential-group-members";

/**
 * Incluir pessoas no grupo. O back recusa o pedido inteiro se alguém não puder entrar;
 * nesse caso o modal fica aberto. Montado só aberto.
 *
 * Props:
 * - groupId
 * - options: colaboradores fora do grupo ({ value, label, hint })
 * - onClose
 * - onAdded(group): o detalhe do grupo que o back devolve
 */
export default function AddMembersModal({ groupId, options, onClose, onAdded }) {
  const groupsService = useService("confidentialGroups");
  const [selected, setSelected] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const hasOptions = options.length > 0;

  function close() {
    if (!saving) onClose();
  }

  async function save() {
    if (selected.length === 0) {
      setError("Escolha pelo menos um colaborador.");
      return;
    }
    setSaving(true);
    try {
      const result = await groupsService.addMembers(groupId, selected);
      toast.success(selected.length === 1 ? "Pessoa incluída no grupo." : "Pessoas incluídas no grupo.");
      onAdded(result?.data);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.msg || "Erro ao incluir as pessoas.");
      setSaving(false);
    }
  }

  return (
    <ModalForm
      $showModal
      $setShowModal={close}
      $noClose={saving}
      $isLoading={saving}
      $size="medium"
      $title="Incluir pessoas"
      $contentBackground="N0"
      $actions={[
        { text: "Cancelar", action: close, variant: "outline", isDisabled: saving },
        { text: "Incluir", action: save, variant: "primary", isDisabled: saving || !hasOptions },
      ]}
    >
      <Field $hasError={!!error}>
        <Select
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          $idForm={FIELD_ID}
          $label="Colaboradores"
          $options={options}
          $errors={error}
          $textNoOptions="Nenhum colaborador encontrado"
          isDisabled={!hasOptions}
          placeholder={hasOptions ? "Selecione os colaboradores" : "Todos os colaboradores já estão no grupo"}
          formatOptionLabel={labelWithHint}
          filterOption={searchLabelAndHint}
          value={options.filter((o) => selected.includes(o.value))}
          onChange={(chosen) => {
            setSelected((chosen || []).map((o) => o.value));
            if (error) setError("");
          }}
        />
        <Text
          $text="Só colaboradores com vínculo ativo na empresa. Administradores já veem tudo e não entram em grupos."
          $variant="caption_medium"
          $color="N400"
        />
      </Field>
    </ModalForm>
  );
}
